import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Separator } from '../ui/separator';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Loader2, AlertCircle, CheckCircle2, History, ClipboardList, UserPlus } from 'lucide-react';
import { cn } from '../../lib/utils';
import CustomerForm from './CustomerForm';
import ItemForm from './ItemForm';
import ServiceBillForm from './ServiceBillForm';
import BillSummary from './BillSummary';
import AddModal from '../AddModal/AddModal';
import { laptopAPI, inventoryAPI } from '../../services/api';
import SuccessScreen from '../ui/SuccessScreen';
import BeautifiedErrorAlert from '../ui/BeautifiedErrorAlert';

// Local utility for fast inputs that update parent only on blur/enter
// Local utility for fast inputs that update parent with debounce
const FastInput = ({ value, onValueChange, ...props }) => {
    const [localValue, setLocalValue] = React.useState(value);

    React.useEffect(() => {
        setLocalValue(value);
    }, [value]);

    React.useEffect(() => {
        const handler = setTimeout(() => {
            if (localValue !== value) {
                onValueChange(localValue);
            }
        }, 500); // 500ms debounce

        return () => {
            clearTimeout(handler);
        };
    }, [localValue, value, onValueChange]);

    return (
        <Input
            {...props}
            value={localValue}
            onChange={e => setLocalValue(e.target.value)}
        />
    );
};

const EditBillModal = ({
    open,
    onOpenChange,
    selectedBill,
    newBill,
    onBillChange,
    onBillTypeChange,
    onAddItem,
    onUpdateItem,
    onRemoveItem,
    totals,
    backendTotals,
    products,
    onUpdate,
    isLoading = false,
    onCustomerSearch,
    onAddServiceBill,
    onUpdateServiceBill,
    onRemoveServiceBill,
    loadProducts,
    modalMessage = null, // Prop from parent (BillPage.js)
    modalErrors = {}, // Backend validation errors from parent
    onClearModalErrors = null // Callback to clear backend errors
}) => {
    const [customerSearchMessage, setCustomerSearchMessage] = React.useState(null);
    const [customerSearchType, setCustomerSearchType] = React.useState(null); // 'success', 'error', or 'info'

    const [showAddModal, setShowAddModal] = React.useState(false);
    const [addModalType, setAddModalType] = React.useState('product');
    const [addNotification, setAddNotification] = React.useState(null);

    const [localErrors, setLocalErrors] = React.useState({});
    const [localSubmitError, setLocalSubmitError] = React.useState(null);
    const [countdown, setCountdown] = React.useState(3);

    // Merge backend errors with local errors for display
    const allErrors = React.useMemo(() => ({
        ...localErrors,
        ...modalErrors
    }), [localErrors, modalErrors]);

    React.useEffect(() => {
        let timer;
        if (modalMessage?.type === 'success' && countdown > 0) {
            timer = setInterval(() => setCountdown(c => c - 1), 1000);
        }
        return () => clearInterval(timer);
    }, [modalMessage?.type, countdown]);

    // Clear errors when modal closes
    React.useEffect(() => {
        if (!open) {
            setLocalErrors({});
            if (onClearModalErrors) {
                onClearModalErrors();
            }
        }
    }, [open, onClearModalErrors]);

    const showNotification = (type, message) => {
        setAddNotification({ type, message });
        setTimeout(() => setAddNotification(null), 5000);
    };

    const handleAddCategory = async (categoryData) => {
        try {
            await inventoryAPI.createCategory(categoryData);
            if (loadProducts) await loadProducts();
            // Success feedback handled in AddModal now
        } catch (err) {
            console.error('Failed to save category:', err);
            // Throwing back to AddModal which now handles its own notifications
            throw err;
        }
    };

    const handleAddProduct = async (productData) => {
        if (!productData) { // Bulk upload reload
            if (loadProducts) await loadProducts();
            showNotification('success', 'Products uploaded and list refreshed!');
            return;
        }
        try {
            await laptopAPI.createProduct(productData);
            if (loadProducts) await loadProducts();
            // Success feedback handled in AddModal now
        } catch (err) {
            console.error('Failed to add product:', err);
            // Throwing back to AddModal which now handles its own notifications
            throw err;
        }
    };

    const handleConfirm = () => {
        setLocalErrors({});
        setLocalSubmitError(null);
        const newLocalErrors = {};

        const isIntegerLike = (n) =>
            Number.isFinite(n) && Math.abs(n - Math.round(n)) < 1e-9;

        const paidAmount = Number(newBill.customerPaidAmount ?? 0) || 0;
        const grandTotal = Number(backendTotals?.grandTotal ?? totals?.grandTotal ?? 0) || 0;
        const computedPendingAmount = grandTotal - paidAmount;

        // Customer Info Validations
        if (!['Bill', 'Invoice'].includes(newBill.billType)) {
            newLocalErrors.billType = 'Transaction Type is required';
        }
        if (!['B2C', 'B2B'].includes(newBill.businessSaleType)) {
            newLocalErrors.businessSaleType = 'Customer Class is required';
        }

        if (!newBill.customerName?.trim()) newLocalErrors.customerName = 'Customer Name is required';
        if (!newBill.customerContactNumber?.trim()) newLocalErrors.customerContactNumber = 'Contact Number is required';
        if (!newBill.billingAddressLine?.trim()) newLocalErrors.billingAddressLine = 'Address is required';
        if (!newBill.billingCity?.trim()) newLocalErrors.billingCity = 'City is required';
        if (!newBill.billingState?.trim()) newLocalErrors.billingState = 'State is required';
        if (!newBill.billingPincode?.trim()) newLocalErrors.billingPincode = 'Pincode is required';

        if (!newBill.orderDate?.trim()) newLocalErrors.orderDate = 'Order Date is required';
        if (newBill.isDealerWarranty && !newBill.orderExpiryDate?.trim()) {
            newLocalErrors.orderExpiryDate = 'Order Expiry Date is required';
        }

        // Payment amount validations (Paid + Pending)
        if (paidAmount < 0) newLocalErrors.customerPaidAmount = 'Paid amount cannot be negative';
        if (!isIntegerLike(paidAmount)) newLocalErrors.customerPaidAmount = 'Paid amount must be an integer';

        if (computedPendingAmount < 0) newLocalErrors.customerPendingBill = 'Pending amount cannot be negative';
        if (!isIntegerLike(computedPendingAmount)) newLocalErrors.customerPendingBill = 'Pending amount must be an integer';

        // Item Validations
        let isMissingSku = false;

        const anyItemsProvided = Array.isArray(newBill.items) && newBill.items.some((item) => {
            if (!item) return false;
            return (
                !!item.productId ||
                Number(item.quantitySold) > 0 ||
                Number(item.sellingPrice) > 0 ||
                (item.skuProductCodes || []).length > 0
            );
        });

        if (anyItemsProvided) {
            newBill.items.forEach((item, index) => {
                const isRowProvided = item && (
                    !!item.productId ||
                    Number(item.quantitySold) > 0 ||
                    Number(item.sellingPrice) > 0 ||
                    (item.skuProductCodes || []).length > 0
                );
                if (!isRowProvided) return;

                if (!item.productId) newLocalErrors[`item_${index}_productId`] = 'Required';
                if (!item.quantitySold || Number(item.quantitySold) <= 0) newLocalErrors[`item_${index}_quantitySold`] = 'Qty > 0';
                if (!item.sellingPrice || Number(item.sellingPrice) <= 0) newLocalErrors[`item_${index}_sellingPrice`] = 'Price > 0';

                if (item.productId && Number(item.quantitySold) > 0) {
                    const product = products.find(p => p.productId === item.productId);
                    const isSerializedProduct = product?.productType === 'SERIALIZED';
                    const requiredSkuCount = Number(item.quantitySold) || 0;
                    const selectedSkuCount = (item.skuProductCodes || []).length;

                    if (isSerializedProduct && selectedSkuCount < requiredSkuCount) {
                        isMissingSku = true;
                        newLocalErrors[`item_${index}_skuProductCodes`] = `Requires ${requiredSkuCount} SKUs`;
                    }
                }
            });
        }

        const anyServicesProvided = Array.isArray(newBill.serviceBills) && newBill.serviceBills.some((sb) => {
            if (!sb) return false;
            return (
                !!sb.description?.trim() ||
                Number(sb.quantity) > 0 ||
                Number(sb.serviceCost) > 0
            );
        });

        if (anyServicesProvided) {
            newBill.serviceBills.forEach((sb, index) => {
                const isRowProvided = sb && (
                    !!sb.description?.trim() ||
                    Number(sb.quantity) > 0 ||
                    Number(sb.serviceCost) > 0
                );
                if (!isRowProvided) return;

                if (!sb.description?.trim()) newLocalErrors[`service_${index}_description`] = 'Required';
                if (!sb.quantity || Number(sb.quantity) <= 0) newLocalErrors[`service_${index}_quantity`] = 'Qty > 0';
                if (!sb.serviceCost || Number(sb.serviceCost) <= 0) newLocalErrors[`service_${index}_serviceCost`] = 'Cost > 0';
            });
        }

        if (!newBill.paymentMethod) newLocalErrors.paymentMethod = 'Payment method is required';
        if (newBill.paymentMethod !== 'CASH' && !newBill.transactionReferenceNumber?.trim()) {
            newLocalErrors.transactionReferenceNumber = 'Transaction reference is required for non-cash payments';
        }
        if (!newBill.paymentStatus) newLocalErrors.paymentStatus = 'Payment status is required';
        if (newBill.paymentStatus === 'REFUNDED' && !newBill.cancellationReason?.trim()) {
            newLocalErrors.cancellationReason = 'Cancellation reason is required';
        }

        if (Object.keys(newLocalErrors).length > 0 || isMissingSku) {
            setLocalErrors(newLocalErrors);
            if (isMissingSku) {
                setLocalSubmitError('Please assign tracking IDs (SKUs) to all serialized products before saving.');
            } else {
                setLocalSubmitError('Please check the highlighted fields above; the request cannot proceed.');
            }
            return;
        }

        onUpdate();
    };

    const handleOpenChange = (newOpen) => {
        if (!isLoading) {
            onOpenChange(newOpen);
        }
    };

    const hasItems = Array.isArray(newBill.items) && newBill.items.length > 0 &&
        newBill.items.some(item => item.productId && item.quantitySold > 0 && item.sellingPrice > 0);
    const hasServices = Array.isArray(newBill.serviceBills) && newBill.serviceBills.length > 0 &&
        newBill.serviceBills.some(sb => sb.description && sb.serviceCost > 0);
    const isServiceOnly = hasServices && !hasItems;

    const paidAmountValue = Number(newBill.customerPaidAmount ?? 0) || 0;
    const effectiveGrandTotal = Number(backendTotals?.grandTotal ?? totals?.grandTotal ?? 0) || 0;
    const computedPendingAmountValue = effectiveGrandTotal - paidAmountValue;
    const computedTotalItemsCount =
        (Array.isArray(newBill.items)
            ? newBill.items.reduce((sum, item) => {
                const qty = Number(item?.quantitySold) || 0;
                const hasLine = (Number(item?.sellingPrice) || 0) > 0 || !!item?.productId;
                return sum + (hasLine && qty > 0 ? qty : 0);
            }, 0)
            : 0)
        +
        (Array.isArray(newBill.serviceBills)
            ? newBill.serviceBills.reduce((sum, sb) => {
                const qty = Number(sb?.quantity);
                const hasLine = !!sb?.description?.trim() || (Number(sb?.serviceCost) || 0) > 0;
                const normalizedQty = Number.isFinite(qty) && qty > 0 ? qty : 0;
                return sum + (hasLine ? normalizedQty : 0);
            }, 0)
            : 0);

    const calculatedStatus = React.useMemo(() => {
        if (newBill.paymentStatus === 'REFUNDED' || newBill.paymentStatus === 'CANCELLED') {
            return newBill.paymentStatus;
        }
        const total = totals.grandTotal || 0;
        const paid = newBill.customerPaidAmount || 0;
        return paid >= total ? 'PAID' : 'PENDING';
    }, [newBill.paymentStatus, newBill.customerPaidAmount, totals.grandTotal]);

    if (!selectedBill) return null;

    return (
        <>
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className={cn(
                "overflow-hidden flex flex-col p-0 transition-all duration-500 rounded-[3rem] border-0 shadow-2xl ring-1 ring-white/20 dark:bg-slate-950 dark:ring-white/10 dark:text-slate-100",
                modalMessage?.type === 'success' ? "max-w-md max-h-[500px]" : "max-w-[1100px] max-h-[98vh]"
            )}>
              {modalMessage?.type === 'success' ? (
                <SuccessScreen 
                    message={`${newBill.billType} Updated!`}
                    subMessage={`Finalizing ${newBill.billType} state...`}
                    countdown={countdown}
                    onAction={() => setCountdown(0)}
                />
              ) : (
                <>
                <DialogHeader className="p-8 pb-4 border-b bg-white/40 dark:bg-slate-900/60 dark:border-slate-800 backdrop-blur-3xl sticky top-0 z-40">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-2">
                        <div className="flex items-center gap-5">
                            <div className="relative group">
                                <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                                <div className="relative p-3.5 rounded-2xl bg-[#ff6f00]/10 text-[#ff6f00] shadow-sm transform hover:-rotate-3 transition-transform duration-300">
                                    <History className="w-8 h-8" />
                                </div>
                            </div>
                            <div>
                                <DialogTitle className="text-3xl font-black tracking-tight text-slate-800 dark:text-white flex items-center gap-2">
                                    Edit {isServiceOnly ? 'Service Invoice' : newBill.billType}
                                    <span className="text-sm font-black px-3 py-1 bg-[#FF7D00]/10 text-[#FF7D00] rounded-full uppercase tracking-widest ml-1 shadow-sm">Modifying</span>
                                </DialogTitle>
                                <DialogDescription className="text-sm font-bold text-[#ff6f00] mt-1 flex items-center gap-1.5 uppercase tracking-tighter opacity-80">
                                    <ClipboardList className="w-3.5 h-3.5" />
                                    #{selectedBill.invoiceNumber || selectedBill.orderId}
                                </DialogDescription>
                            </div>
                        </div>
                        {(() => {
                            const user = JSON.parse(localStorage.getItem('user') || '{}');
                            const userName = (user.firstName && user.lastName) ? `${user.firstName} ${user.lastName}` : (user.username || 'System Admin');
                            return (
                                <div className="inline-flex items-center px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-500/20 shadow-sm self-start sm:self-center">
                                    <UserPlus className="w-3.5 h-3.5 mr-2" />
                                    Assigned: {userName}
                                </div>
                            );
                        })()}
                    </div>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-6 py-6 scroll-smooth">


                {/* Local or Modal Errors */}
                {(localSubmitError || modalMessage?.type === 'error') && (
                    <div className="mb-6 animate-in slide-in-from-top-4 duration-300">
                        <BeautifiedErrorAlert 
                            error={localSubmitError || modalMessage?.text} 
                            onDismiss={() => {
                                setLocalSubmitError(null);
                            }} 
                        />
                    </div>
                )}
                
                {addNotification && (
                    <div className="mb-8 p-0 animate-in slide-in-from-top-4 duration-500">
                        <div className={cn(
                            "flex items-start gap-4 p-5 rounded-2xl border shadow-lg shadow-black/5",
                            addNotification?.type === 'success' ? "bg-emerald-50 border-emerald-100 text-emerald-900" : "bg-rose-50 border-rose-100 text-rose-900"
                        )}>
                            <div className={cn("p-2 rounded-xl shrink-0 mt-0.5", addNotification?.type === 'success' ? "bg-emerald-100/50" : "bg-rose-100/50")}>
                                {addNotification?.type === 'success' ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <AlertCircle className="h-5 w-5 text-rose-600" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-black uppercase tracking-wider leading-none mb-1.5 pt-1.5">
                                    {addNotification?.type === 'success' ? 'Success' : 'Attention Required'}
                                </p>
                                <p className="text-xs font-bold leading-relaxed opacity-80">{addNotification?.message}</p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-4">
                            <Label className="text-xs font-black uppercase tracking-widest text-slate-400">Transaction Type</Label>
                            <div className={cn(
                                "flex p-1 bg-slate-100/50 rounded-2xl w-full border border-slate-200",
                                allErrors.billType && "border-rose-400 bg-rose-50/30"
                            )}>
                                <button
                                    type="button"
                                    onClick={() => onBillTypeChange('Bill')}
                                    className={cn(
                                        "flex-1 py-3 px-6 rounded-xl text-sm font-black transition-all duration-300",
                                        newBill.billType === 'Bill'
                                            ? "bg-white dark:bg-slate-800 shadow-xl shadow-orange-500/5 dark:shadow-none text-[#FF7D00] ring-1 ring-orange-500/10 dark:ring-white/10"
                                            : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-white/40 dark:hover:bg-slate-800/40"
                                    )}
                                >
                                    📄 Retail Bill
                                </button>
                                <button
                                    type="button"
                                    onClick={() => onBillTypeChange('Invoice')}
                                    className={cn(
                                        "flex-1 py-3 px-6 rounded-xl text-sm font-black transition-all duration-300",
                                        newBill.billType === 'Invoice'
                                            ? "bg-white dark:bg-slate-800 shadow-xl shadow-orange-500/5 dark:shadow-none text-[#FF7D00] ring-1 ring-orange-500/10 dark:ring-white/10"
                                            : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-white/40 dark:hover:bg-slate-800/40"
                                    )}
                                >
                                    💰 Tax Invoice
                                </button>
                            </div>
                            {allErrors.billType && (
                                <p className="text-[10px] font-black text-rose-500 ml-1 uppercase tracking-tighter">
                                    {allErrors.billType}
                                </p>
                            )}
                        </div>

                        <div className="space-y-4">
                            <Label className="text-xs font-black uppercase tracking-widest text-slate-400">Customer Class</Label>
                            <div className={cn(
                                "flex p-1 bg-slate-100/50 rounded-2xl w-full border border-slate-200",
                                allErrors.businessSaleType && "border-rose-400 bg-rose-50/30"
                            )}>
                                <button
                                    type="button"
                                    onClick={() => onBillChange('businessSaleType', 'B2C')}
                                    className={cn(
                                        "flex-1 py-3 px-6 rounded-xl text-sm font-black transition-all duration-300",
                                        newBill.businessSaleType === 'B2C'
                                            ? "bg-white dark:bg-slate-800 shadow-xl shadow-[#ff6f00]/5 dark:shadow-none text-[#ff6f00] ring-1 ring-[#ff6f00]/10 dark:ring-white/10"
                                            : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-white/40 dark:hover:bg-slate-800/40"
                                    )}
                                >
                                    👤 Individual
                                </button>
                                <button
                                    type="button"
                                    onClick={() => onBillChange('businessSaleType', 'B2B')}
                                    className={cn(
                                        "flex-1 py-3 px-6 rounded-xl text-sm font-black transition-all duration-300",
                                        newBill.businessSaleType === 'B2B'
                                            ? "bg-white dark:bg-slate-800 shadow-xl shadow-[#ff6f00]/5 dark:shadow-none text-[#ff6f00] ring-1 ring-[#ff6f00]/10 dark:ring-white/10"
                                            : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-white/40 dark:hover:bg-slate-800/40"
                                    )}
                                >
                                    🏢 Business
                                </button>
                            </div>
                            {allErrors.businessSaleType && (
                                <p className="text-[10px] font-black text-rose-500 ml-1 uppercase tracking-tighter">
                                    {allErrors.businessSaleType}
                                </p>
                            )}
                        </div>
                    </div>


                    {newBill.businessSaleType === 'B2B' && (
                        <div className="space-y-2">
                            <Label htmlFor="editPurchaseOrderNumber">Purchase Order Number</Label>
                            <Input
                                id="editPurchaseOrderNumber"
                                placeholder="Enter PO Number"
                                value={newBill.purchaseOrderNumber || ''}
                                onChange={(e) => onBillChange('purchaseOrderNumber', e.target.value)}
                            />
                        </div>
                    )}

                    <Separator />

                    {customerSearchMessage && (
                        <div className={cn(
                            "p-3 rounded-lg border text-sm",
                            customerSearchType === 'success'
                                ? "bg-green-50 border-green-200 text-green-800"
                                : customerSearchType === 'error'
                                    ? "bg-red-50 border-red-200 text-red-800"
                                    : "bg-blue-50 border-blue-200 text-blue-800"
                        )}>
                            {customerSearchMessage}
                        </div>
                    )}

                    <CustomerForm
                        customer={newBill}
                        onChange={onBillChange}
                        onCustomerSearch={(phone) => {
                            setCustomerSearchMessage(null);
                            if (onCustomerSearch) {
                                onCustomerSearch(phone, setCustomerSearchMessage, setCustomerSearchType);
                            }
                        }}
                        errors={allErrors}
                    />

                    <Separator />


                    <div className="p-4 bg-muted/30 rounded-lg border border-muted-foreground/10 space-y-4">
                        <div className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                id="editIsDealerWarranty"
                                checked={newBill.isDealerWarranty || false}
                                onChange={(e) => onBillChange('isDealerWarranty', e.target.checked)}
                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                            />
                            <Label htmlFor="editIsDealerWarranty" className="text-sm font-medium cursor-pointer">
                                Is this product under dealer warranty?
                            </Label>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <Label htmlFor="editOrderDate">Order Date <span className="text-destructive">*</span></Label>
                                <Input
                                    id="editOrderDate"
                                    type="date"
                                    value={newBill.orderDate || ''}
                                    onChange={(e) => onBillChange('orderDate', e.target.value)}
                                    className={cn("bg-white", allErrors.orderDate && "border-rose-400 bg-rose-50/30 animate-shake")}
                                />
                                {allErrors.orderDate && (
                                    <p className="text-[10px] font-black text-rose-500 ml-1 uppercase tracking-tighter">
                                        {allErrors.orderDate}
                                    </p>
                                )}
                            </div>
                            {newBill.isDealerWarranty && (
                                <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                                    <Label htmlFor="editOrderExpiryDate">
                                        Order Expiry Date <span className="text-destructive">*</span>
                                    </Label>
                                    <Input
                                        id="editOrderExpiryDate"
                                        type="date"
                                        value={newBill.orderExpiryDate || ''}
                                        onChange={(e) => onBillChange('orderExpiryDate', e.target.value)}
                                        className={cn(
                                            "jit-input dark:bg-slate-900 border-slate-200 dark:border-slate-800",
                                            allErrors.orderExpiryDate && "border-rose-400 bg-rose-50/30 animate-shake"
                                        )}
                                    />
                                    {allErrors.orderExpiryDate && (
                                        <p className="text-[10px] font-black text-rose-500 ml-1 uppercase tracking-tighter">
                                            {allErrors.orderExpiryDate}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        {newBill.isDealerWarranty && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in slide-in-from-top-2 duration-300">
                                <div className="space-y-2">
                                    <Label htmlFor="editShipmentDate">Shipment Date</Label>
                                    <Input
                                        id="editShipmentDate"
                                        type="date"
                                        value={newBill.shipmentDate || ''}
                                        onChange={(e) => onBillChange('shipmentDate', e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="editDeliveryDate">Delivery Date</Label>
                                    <Input
                                        id="editDeliveryDate"
                                        type="date"
                                        value={newBill.deliveryDate || ''}
                                        onChange={(e) => onBillChange('deliveryDate', e.target.value)}
                                    />
                                </div>
                            </div>
                        )}
                    </div>


                    <Separator />

                    {/* Payment Method Field */}

                    <div className="space-y-2">
                        <Label htmlFor="editPaymentMethod">Payment Method <span className="text-destructive">*</span></Label>
                        <Select
                            value={newBill.paymentMethod || ''}
                            onValueChange={value => {
                                onBillChange('paymentMethod', value);
                                if (localErrors.paymentMethod) setLocalErrors(prev => ({...prev, paymentMethod: null}));
                            }}
                            required
                        >
                            <SelectTrigger id="editPaymentMethod" className={cn("jit-input dark:bg-slate-900 border-slate-200 dark:border-slate-800", allErrors.paymentMethod && "border-red-400 bg-red-50/50 dark:bg-red-500/10 animate-shake")}>
                                <SelectValue placeholder="Select payment method" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="CASH">Cash</SelectItem>
                                <SelectItem value="UPI">UPI</SelectItem>
                                <SelectItem value="CREDIT_CARD">Credit Card</SelectItem>
                                <SelectItem value="DEBIT_CARD">Debit Card</SelectItem>
                                <SelectItem value="NET_BANKING">Net Banking</SelectItem>
                            </SelectContent>
                        </Select>
                        {allErrors.paymentMethod && <p className="text-xs font-black text-rose-500 ml-1 uppercase">{allErrors.paymentMethod}</p>}
                    </div>

                    {newBill.paymentMethod && newBill.paymentMethod !== 'CASH' && (
                        <div className="space-y-2">
                            <Label htmlFor="editTransactionReferenceNumber">Transaction Number <span className="text-destructive">*</span></Label>
                            <Input
                                id="editTransactionReferenceNumber"
                                placeholder="Enter transaction number (e.g. UPI ID or Ref No)"
                                value={newBill.transactionReferenceNumber || ''}
                                onChange={(e) => {
                                    onBillChange('transactionReferenceNumber', e.target.value);
                                    if (localErrors.transactionReferenceNumber) setLocalErrors(prev => ({...prev, transactionReferenceNumber: null}));
                                }}
                                className={cn("h-12 rounded-xl border-slate-200 transition-all font-bold", allErrors.transactionReferenceNumber && "border-rose-400 bg-rose-50/30 ring-2 ring-rose-500/10")}
                                required
                            />
                            {allErrors.transactionReferenceNumber && <p className="text-xs font-black text-rose-500 ml-1 uppercase">{allErrors.transactionReferenceNumber}</p>}
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label htmlFor="editPaymentStatus">
                            Payment Status <span className="text-muted-foreground mr-2">(Current: {calculatedStatus})</span>
                        </Label>
                        <Select
                            value={newBill.paymentStatus || ''}
                            onValueChange={value => {
                                onBillChange('paymentStatus', value);
                                if (localErrors.paymentStatus) setLocalErrors(prev => ({...prev, paymentStatus: null}));
                            }}
                        >
                            <SelectTrigger id="editPaymentStatus" className={cn("jit-input dark:bg-slate-900 border-slate-200 dark:border-slate-800", allErrors.paymentStatus && "border-red-400 bg-red-50/50 dark:bg-red-500/10 animate-shake")}>
                                <SelectValue placeholder="Mark as Cancelled..." />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="PAID">Paid</SelectItem>
                                <SelectItem value="PENDING">Pending</SelectItem>
                                <SelectItem value="PARTIAL_PAID">Partially Paid</SelectItem>
                                <SelectItem value="REFUNDED">Cancel Order / Refunded</SelectItem>
                            </SelectContent>
                        </Select>
                        {allErrors.paymentStatus && <p className="text-xs font-black text-rose-500 ml-1 uppercase">{allErrors.paymentStatus}</p>}
                    </div>

                    {newBill.paymentStatus === 'REFUNDED' && (
                        <div className="space-y-2">
                            <Label htmlFor="cancellationReason">Cancellation Reason <span className="text-destructive">*</span></Label>
                            <Input
                                id="cancellationReason"
                                placeholder="Why is this order being cancelled?"
                                value={newBill.cancellationReason || ''}
                                onChange={(e) => {
                                    onBillChange('cancellationReason', e.target.value);
                                    if (localErrors.cancellationReason) setLocalErrors(prev => ({...prev, cancellationReason: null}));
                                }}
                                className={cn("h-12 rounded-xl border-slate-200 transition-all font-bold", allErrors.cancellationReason && "border-rose-400 bg-rose-50/30 ring-2 ring-rose-500/10")}
                                required
                            />
                            {allErrors.cancellationReason && <p className="text-xs font-black text-rose-500 ml-1 uppercase">{allErrors.cancellationReason}</p>}
                        </div>
                    )}

                    <Separator />

                    <div className="space-y-4 bg-muted/10 dark:bg-slate-900/50 p-4 rounded-lg border border-muted-foreground/10 dark:border-slate-800">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold flex items-center gap-2">
                                <span className="bg-[#ff6f00]/10 p-1 rounded">📄</span>
                                Physical Bill Details (Optional)
                            </h3>
                            <div className="flex items-center space-x-2 bg-white/50 dark:bg-slate-800/50 px-3 py-1 rounded-full border border-[#ff6f00]/20">
                                <input
                                    type="checkbox"
                                    id="editPhysicalBillAvailableFlg"
                                    checked={newBill.physicalBillAvailableFlg || false}
                                    onChange={(e) => onBillChange('physicalBillAvailableFlg', e.target.checked)}
                                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                />
                                <Label htmlFor="editPhysicalBillAvailableFlg" className="text-xs font-bold text-[#ff6f00] cursor-pointer">
                                    Bill Available?
                                </Label>
                            </div>
                        </div>

                        {newBill.physicalBillAvailableFlg && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
                                <div className="space-y-2">
                                    <Label htmlFor="editPhysicalBillNumber">Physical Bill Number</Label>
                                    <Input
                                        id="editPhysicalBillNumber"
                                        placeholder="Enter physical bill number"
                                        value={newBill.physicalBillNumber || ''}
                                        onChange={(e) => onBillChange('physicalBillNumber', e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="editPhysicalBillFile">Update Physical Bill</Label>
                                    <Input
                                        id="editPhysicalBillFile"
                                        type="file"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        className="cursor-pointer bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
                                        onChange={(e) => onBillChange('physicalBillFile', e.target.files[0])}
                                    />
                                    {newBill.physicalBillFile ? (
                                        <p className="text-xs text-green-600 font-medium">
                                            Selected: {newBill.physicalBillFile.name}
                                        </p>
                                    ) : newBill.physicalBillFileName ? (
                                        <p className="text-xs text-blue-600 font-medium italic">
                                            Current: {newBill.physicalBillFileName}
                                        </p>
                                    ) : null}
                                </div>
                            </div>
                        )}
                    </div>

                    <ItemForm
                        items={newBill.items}
                        billType={newBill.billType}
                        onAddItem={onAddItem}
                        onUpdateItem={onUpdateItem}
                        onRemoveItem={onRemoveItem}
                        products={products}
                        onAddNewProduct={() => {
                            setAddModalType('product');
                            setShowAddModal(true);
                        }}
                        onAddNewCategory={() => {
                            setAddModalType('category');
                            setShowAddModal(true);
                        }}
                        errors={allErrors}
                    />

                    <Separator className="my-4" />

                    <ServiceBillForm
                        serviceBills={newBill.serviceBills || []}
                        billType={newBill.billType}
                        onAdd={onAddServiceBill}
                        onUpdate={onUpdateServiceBill}
                        onRemove={onRemoveServiceBill}
                        errors={allErrors}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                        <div className="space-y-4 bg-muted/20 p-4 rounded-xl border border-dashed border-muted-foreground/20">
                            <h4 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                                🏷️ Global Adjustments
                            </h4>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label htmlFor="editDiscountPercent" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                        Discount (%)
                                    </label>
                                    <div className="relative">
                                        <FastInput
                                            id="editDiscountPercent"
                                            type="number"
                                            min={0}
                                            max={100}
                                            step={1}
                                            value={newBill.discountPercent || ''}
                                            onValueChange={val => onBillChange('discountPercent', Math.max(0, Math.min(100, parseFloat(val) || 0)))}
                                            placeholder="0"
                                            className="pr-8 font-semibold bg-white"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-bold">%</span>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="editShippingFee" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                        Shipping Fee
                                    </label>
                                    <div className="relative">
                                        <FastInput
                                            id="editShippingFee"
                                            type="number"
                                            min={0}
                                            value={newBill.shippingFee || ''}
                                            onValueChange={val => onBillChange('shippingFee', Math.max(0, parseFloat(val) || 0))}
                                            placeholder="0"
                                            className="pl-8 font-semibold bg-white"
                                        />
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-bold">Rs</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4 bg-muted/20 p-4 rounded-xl border border-dashed border-muted-foreground/20">
                            <h4 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                                <ClipboardList className="w-4 h-4" /> Special Notes / Internal Remarks
                            </h4>
                            <Textarea
                                placeholder="Add any special instructions or internal notes here..."
                                value={newBill.customerNotes || ''}
                                onChange={(e) => onBillChange('customerNotes', e.target.value)}
                                className="min-h-[80px] bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
                            />
                        </div>

                        {/* Moved Paid/Pending Amount Row */}
                        <div className="col-span-1 md:col-span-2 space-y-4 bg-blue-50/30 p-4 rounded-xl border border-dashed border-blue-200/50">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <Label htmlFor="editCustomerPaidAmount" className="text-xs font-black uppercase tracking-widest text-blue-600/70">Paid Amount (Rs)</Label>
                                    <FastInput
                                        id="editCustomerPaidAmount"
                                        type="number"
                                        placeholder="0.00"
                                        value={paidAmountValue}
                                        onValueChange={(val) => onBillChange('customerPaidAmount', parseFloat(val) || 0)}
                                        className={cn(
                                            "h-12 bg-white font-bold",
                                            allErrors.customerPaidAmount && "border-rose-400 bg-rose-50/30 animate-shake"
                                        )}
                                    />
                                    {allErrors.customerPaidAmount && (
                                        <p className="text-[10px] font-black text-rose-500 ml-1 uppercase tracking-tighter">
                                            {allErrors.customerPaidAmount}
                                        </p>
                                    )}
                                    <p className="text-[10px] text-blue-500/60 font-medium italic">Note: Please add the paid amount in increments.</p>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="editCustomerPendingBill" className="text-xs font-black uppercase tracking-widest text-blue-600/70">Pending Amount (Rs)</Label>
                                    <FastInput
                                        id="editCustomerPendingBill"
                                        type="number"
                                        placeholder="0.00"
                                        value={Number.isFinite(computedPendingAmountValue) ? computedPendingAmountValue : 0}
                                        onValueChange={() => { }}
                                        readOnly
                                        className={cn(
                                            "h-12 bg-white font-bold",
                                            allErrors.customerPendingBill && "border-rose-400 bg-rose-50/30 animate-shake"
                                        )}
                                    />
                                    {allErrors.customerPendingBill && (
                                        <p className="text-[10px] font-black text-rose-500 ml-1 uppercase tracking-tighter">
                                            {allErrors.customerPendingBill}
                                        </p>
                                    )}
                                    <p className="text-[10px] text-blue-500/60 font-medium italic">Auto-calculated from Paid Amount.</p>
                                    <div className="pt-2">
                                        <BillSummary
                                            totals={totals}
                                            backendTotals={backendTotals}
                                            discountPercent={newBill.discountPercent}
                                            shippingFee={newBill.shippingFee}
                                            paidAmount={newBill.customerPaidAmount}
                                            advanceAmount={newBill.advanceAmount}
                                            isAdvanceMerged={newBill.isAdvanceMerged}
                                            totalItemsCount={computedTotalItemsCount}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
                </div>

                <DialogFooter className="p-6 pt-4 border-t bg-slate-50/50 dark:bg-slate-900/60 dark:border-slate-800 sticky bottom-0 z-30 sm:justify-end items-center gap-4 shadow-[0_-4px_20px_-5px_rgba(0,0,0,0.05)]">
                    <div className="flex gap-4 w-full sm:w-auto">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={isLoading}
                            className="flex-1 sm:flex-none h-12 px-8 rounded-xl border-slate-200 font-black text-slate-500 flex-1"
                        >
                            Discard
                        </Button>
                        <Button
                            type="button"
                            onClick={handleConfirm}
                            disabled={isLoading}
                            className="flex-1 sm:flex-none h-14 px-16 rounded-2xl bg-[#FF7D00] hover:brightness-110 active:scale-95 text-white font-black uppercase tracking-widest shadow-2xl shadow-orange-500/20 transition-all duration-300 flex-1 sm:grow-0"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                    Updating...
                                </>
                            ) : (
                                `Update ${newBill.billType}`
                            )}
                        </Button>
                    </div>
                </DialogFooter>
                </>
              )}
            </DialogContent>
        </Dialog>

        {showAddModal && (
            <AddModal
                modalType={addModalType}
                onClose={() => setShowAddModal(false)}
                onAdd={addModalType === 'category' ? handleAddCategory : handleAddProduct}
            />
        )}
        </>
    );
};

export default React.memo(EditBillModal);